import { HandHeart } from "lucide-react";

import { Checkbox } from "@/shared/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { cn } from "@/shared/lib/utils";

import type { AcqOpportunityCard } from "../../api/acqService";
import {
  canEnrichCard,
  FEEDBACK_LABEL,
  freshnessText,
  happenedDaysAgo,
  intentTypeLabel,
  sourceLabel,
  stateLabel,
} from "../../model/acqLabels";
import { AssignControl } from "./AssignControl";
import { deadlineText } from "./OpportunityCard";
import { cardProducts as products, ProductChips } from "./ProductChips";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 线索库 = 一张能扫的表(契约 §13.6:老板看完演示机的第④条反馈)
 * ═══════════════════════════════════════════════════════════════════════════
 * 上一版这一页是一墙紧凑卡。卡回答的是「这一条该不该做」—— 那是**今日商机**的问题;
 * 线索库要回答的是另外两个:**「我那 200 条里哪些还没人跟」**、**「上周那条后来怎么样了」**。
 * 这两个问题都要横着比,而卡墙只能竖着读。
 *
 * ★ 整张卡没消失,它搬进了右侧抽屉:点主体名就能看到事实、推断、产物、五个反馈按钮
 *   —— 表格负责扫,抽屉负责看。
 * ★ 窄屏(390)靠 `Table` 自带的 `overflow-auto` 容器横滑,**不是整页横滑**:
 *   整页能横滑的话,页头和 tab 也跟着跑,用户会以为自己把页面拖坏了。
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function LeadsTable({
  items,
  canAssign,
  meId,
  busyId,
  activeId,
  onOpen,
  onAssign,
  onClaim,
  onPickProduct,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}: {
  items: AcqOpportunityCard[];
  canAssign: boolean;
  meId: number | null;
  busyId: string | null;
  /** 抽屉里正开着的那条 —— 行上给一条高亮,不然关掉抽屉后找不回刚才看的是哪行。 */
  activeId?: string | null;
  onOpen: (card: AcqOpportunityCard) => void;
  onAssign: (id: string, assigneeId: number | null, displayName?: string) => void;
  onClaim: (id: string) => void;
  /**
   * 点「产品」列里的一枚 chip → 切到那个产品的页签(契约 §19.3)。
   * ★ 缺席 ⇒ 那一列就是一排标签(直接渲染这张表的测试/桩里是这样)。
   */
  onPickProduct?: (productCode: string) => void;
  /**
   * 1015 批量补全:勾选列。**三者都给才出这一列**(直接渲染这张表的测试/桩里不给 = 老样子)。
   * 只有「能补全」的行可勾(判据同单条按钮,`canEnrichCard`),其余行的框置灰。
   */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string, checked: boolean) => void;
  onToggleAll?: (ids: string[], checked: boolean) => void;
}) {
  const selectable = !!(selectedIds && onToggleSelect && onToggleAll);
  const enrichable = selectable ? items.filter(canEnrichCard).map((c) => c.id) : [];
  const allChecked = enrichable.length > 0 && enrichable.every((id) => selectedIds!.has(id));
  return (
    <div className="rounded-xl border bg-card">
      {/* ★ 390 下这张表只露得出「主体」一列,其余要横拖 —— 真渲染时看到的就是一列公司名,
          没有任何东西说明右边还有东西。这一行只在窄屏出现(`sm:hidden`)。 */}
      <p className="border-b px-3 py-1.5 text-2xs text-muted-foreground sm:hidden">
        左右滑动看来源、产品、分派等列
      </p>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {selectable && (
              <TableHead className="w-8">
                <Checkbox
                  aria-label="全选可补全的线索"
                  checked={allChecked}
                  disabled={enrichable.length === 0}
                  onCheckedChange={(v) => onToggleAll!(enrichable, v === true)}
                />
              </TableHead>
            )}
            <TableHead className="min-w-[13rem]">主体</TableHead>
            <TableHead>来源</TableHead>
            {/* 0917 批次三(契约 §14.3):「意向」与「来源」一起回答"这条是什么" ——
                来源说的是在哪儿捡到的,意向说的是**这个人想干什么**。 */}
            <TableHead>意向</TableHead>
            <TableHead>产品</TableHead>
            <TableHead className="text-right">优先级</TableHead>
            <TableHead>状态</TableHead>
            <TableHead>反馈</TableHead>
            <TableHead>分派</TableHead>
            <TableHead>截止 / 发生</TableHead>
            <TableHead>最近动作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((card) => (
            <TableRow
              key={card.id}
              data-state={activeId === card.id ? "selected" : undefined}
              className="cursor-pointer"
              onClick={() => onOpen(card)}
            >
              {selectable && (
                <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                  {/* 不能补全的行不摆框:置灰的框和能勾的框在浅色皮肤下几乎分不出来(1015 本地走查)。 */}
                  {canEnrichCard(card) && (
                    <Checkbox
                      aria-label={`选择 ${card.subjectName}`}
                      checked={selectedIds!.has(card.id)}
                      onCheckedChange={(v) => onToggleSelect!(card.id, v === true)}
                    />
                  )}
                </TableCell>
              )}
              <TableCell className="max-w-[22rem]">
                {/* ★ 主体名做成按钮而不是整行加 onClick 就完事:键盘能 Tab 到它,
                    读屏也读得出"这是个能打开的东西"。整行的 onClick 是给鼠标的便利,
                    不是唯一入口。 */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(card);
                  }}
                  className="line-clamp-2 break-words text-left text-sm font-semibold hover:text-primary hover:underline"
                >
                  {card.subjectName}
                </button>
                {card.region && (
                  <span className="mt-0.5 block truncate text-2xs text-muted-foreground">
                    {card.region}
                  </span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                {/* 跨源合卡后一家公司一张卡(契约 §14.2):这一格报的是**几个来源**,
                    具体哪几个在抽屉里的「这家最近」摆 —— 表格一格塞三个源名会把列撑爆。 */}
                {(card.sources?.length ?? 0) > 1
                  ? `${card.sources!.length} 个来源`
                  : sourceLabel(card.source)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                {card.intentType ? (
                  <span className="rounded-full bg-foreground/10 px-1.5 py-px text-2xs font-semibold text-foreground/80">
                    {intentTypeLabel(card.intentType)}
                  </span>
                ) : (
                  // 精判没给意向就留空 —— 与产品那一格同则:空着本身是给运营看的信号。
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              {/* ★★ 0918 批次八(契约 §19.3):这一格摆**全部**产品,不再只摆精判选中的那一个。
                  ★ `flex-wrap` 而不是 `whitespace-nowrap`:三枚 chip 挤在一行会把这一列撑到
                    把「优先级」之后的列全推出屏外(390 下整张表本来就要横拖)。
                  ★ chip 自己掐了冒泡(见 `ProductChips`),所以点产品**不会**顺带打开抽屉。 */}
              <TableCell className="text-xs">
                {products(card).length > 0 ? (
                  <div className="flex max-w-[12rem] flex-wrap items-center gap-1 text-2xs">
                    <ProductChips card={card} onPick={onPickProduct} />
                  </div>
                ) : (
                  // ★ 精判选不出产品时留空(契约 §13.7:越界就是空串,不猜)。
                  //   面上写「—」而不是猜一个 —— 这一格空着本身就是要给运营看的信号。
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right text-sm font-bold tabular-nums text-primary">
                {card.priorityScore}
              </TableCell>
              {/* ★ 「状态」hover 出 `stateReason`(契约 §14.3):线索库里最常被问的是
                  「上周那条后来怎么了」—— 状态答了"在哪一步",原因才答得出"因为谁做了什么"。
                  ★ 缺席不给 title(空 tooltip 比没有更像坏了),同时加 `cursor-help` 让人
                  知道这一格有东西可看。 */}
              <TableCell
                className={cn("whitespace-nowrap text-xs", card.stateReason && "cursor-help")}
                title={card.stateReason || undefined}
              >
                {stateLabel(card.state)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                {card.feedbackCode ? (
                  // ★ 反馈单独一列、不并进状态:wrong_person / bad_phone 也落 LOST,
                  //   只看状态会把**采集端的毛病**读成销售谈输了(acqLabels 的老账)。
                  <span
                    className={cn(
                      "rounded-full border px-1.5 py-px text-2xs",
                      card.feedbackCode === "won" && "border-success/40 text-success",
                      (card.feedbackCode === "wrong_person" || card.feedbackCode === "bad_phone") &&
                        "border-warning/40 text-warning"
                    )}
                  >
                    {FEEDBACK_LABEL[card.feedbackCode]}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              {/* ★ 分派那一格里点下拉**不能顺带打开抽屉**:分派是"派给谁",不是"我要看它"。 */}
              <TableCell onClick={(e) => e.stopPropagation()}>
                <AssignControl
                  card={card}
                  canAssign={canAssign}
                  meId={meId}
                  busy={busyId === card.id}
                  onAssign={onAssign}
                  onClaim={onClaim}
                />
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                <DeadlineCell card={card} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                <LastActionCell card={card} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * 「截止 / 发生」一格:**有死线报死线,没死线报什么时候发生的**。
 * ★ 两件事共用一列是有意的:它们回答同一个问题 ——「这条还来得及吗」。
 *   招标截止过了这单就没了;知乎提问没有死线,但半个月前的问题早被人答完了。
 */
function DeadlineCell({ card }: { card: AcqOpportunityCard }) {
  if (card.daysLeft != null) {
    return (
      <span
        className={
          card.daysLeft < 0
            ? "text-muted-foreground line-through"
            : card.daysLeft <= 3
              ? "font-semibold text-destructive"
              : "font-semibold text-warning"
        }
        title={card.deadlineAt ? `截止 ${card.deadlineAt.replace("T", " ")}` : undefined}
      >
        {deadlineText(card.daysLeft)}
      </span>
    );
  }
  const fresh = freshnessText(happenedDaysAgo(card));
  return <span className="text-muted-foreground">{fresh || "—"}</span>;
}

/**
 * 「最近动作」一格。
 *
 * ★★ 这一格**只说做了什么,不说什么时候做的** —— 因为 DTO 里没有那个时刻:
 *   接手时间藏在 `ai_brief.takenAt`(契约 §12.4 明说"不加列"),反馈时间只有
 *   `state_changed_at` 而它没出接口。编一个时间戳(比如拿 `detectedAt` 充数)
 *   会让「上周那条后来怎么样了」这个问题得到一个**看起来精确的错答案**。
 *   要真时间,得先在 `AcqOpportunityCard` 上补 `takenAt` / `feedbackAt` 两格(已挂账)。
 */
function LastActionCell({ card }: { card: AcqOpportunityCard }) {
  if (card.feedbackCode) return <span>反馈「{FEEDBACK_LABEL[card.feedbackCode]}」</span>;
  if (card.taken) {
    return (
      <span className="inline-flex items-center gap-1 text-foreground">
        <HandHeart className="h-3.5 w-3.5" />
        已接手
      </span>
    );
  }
  return <span>还没动</span>;
}
